import React, { useState, useEffect } from 'react';
import type { Plant } from '../types';

interface FlipCardProps {
  plant: Plant;
  index: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  shuffleMode: boolean;
  onToggleShuffle: () => void;
  isLearned: boolean;
  onToggleLearned: () => void;
}

export const FlipCard: React.FC<FlipCardProps> = ({
  plant, index, total, onPrev, onNext, shuffleMode, onToggleShuffle, isLearned, onToggleLearned,
}) => {
  const [flipped, setFlipped] = useState(false);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [learnedAnim, setLearnedAnim] = useState(false);

  useEffect(() => { setFlipped(false); }, [plant.id]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') onPrev();
      else if (e.key === 'ArrowRight') onNext();
      else if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setFlipped((f) => !f); }
      else if (e.key === 'm' || e.key === 'M') { handleLearnedClick(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onPrev, onNext, onToggleLearned]);

  const handleLearnedClick = () => {
    onToggleLearned();
    setLearnedAnim(true);
    setTimeout(() => setLearnedAnim(false), 600);
  };

  const handleTouchStart = (e: React.TouchEvent) => setTouchStartX(e.touches[0].clientX);
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX;
    if (Math.abs(dx) > 50) dx < 0 ? onNext() : onPrev();
    else setFlipped((f) => !f);
    setTouchStartX(null);
  };

  const progressPct = total > 1 ? (index / (total - 1)) * 100 : 100;

  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-4 py-6 gap-4"
      style={{ background: 'radial-gradient(ellipse at 30% 20%, rgba(104,168,126,0.08) 0%, transparent 60%), radial-gradient(ellipse at 80% 80%, rgba(184,145,58,0.06) 0%, transparent 50%)' }}>

      {/* Header */}
      <div className="w-full max-w-2xl flex items-center justify-between">
        <span className="font-mono text-sm text-creamDim bg-white/70 px-3 py-1 rounded-full border border-border/60 shadow-sm">
          {index + 1} <span className="text-border mx-1">/</span> {total}
        </span>
        <button
          onClick={onToggleShuffle}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200
            ${shuffleMode
              ? 'bg-sage text-white border-sage shadow-btn'
              : 'bg-white/70 text-creamDim border-border/60 hover:border-sage/50 hover:text-sage'}`}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/>
            <polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/>
          </svg>
          Zufall
        </button>
      </div>

      {/* Progress */}
      <div className="w-full max-w-2xl progress-bar">
        <div className="progress-fill" style={{ width: `${progressPct}%` }} />
      </div>

      {/* Flip card */}
      <div
        className="flip-scene w-full max-w-2xl cursor-pointer select-none"
        style={{ height: 'min(60vh, 460px)' }}
        onClick={() => setFlipped((f) => !f)}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        role="button"
        aria-label="Karte umdrehen"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setFlipped((f) => !f); }}
      >
        <div className={`flip-inner ${flipped ? 'flipped' : ''}`}>

          {/* FRONT — image */}
          <div className="flip-front panel overflow-hidden"
            style={{ boxShadow: '0 8px 40px rgba(42,85,52,0.14), 0 2px 12px rgba(0,0,0,0.08)' }}>
            {plant.image_url ? (
              <img
                src={plant.image_url}
                alt={plant.german_name}
                className="w-full h-full object-cover rounded-2xl"
                draggable={false}
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-3 bg-paper"
                style={{ background: 'radial-gradient(ellipse at 50% 40%, rgba(104,168,126,0.10) 0%, transparent 70%)' }}>
                <div className="text-sage/40">
                  <svg width="56" height="56" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17 8C8 10 5.9 16.17 3.82 21.34L5.71 22l1-2.3A4.49 4.49 0 008 20C19 20 22 3 22 3c-1 2-8 3.5-13.5 7"/>
                  </svg>
                </div>
                <p className="plant-name text-creamDim/60 text-sm">Kein Bild vorhanden</p>
              </div>
            )}

            {/* Learned badge on front */}
            {isLearned && (
              <div className="absolute top-3 right-3 bg-emerald-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full shadow-md flex items-center gap-1 pointer-events-none">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Gemerkt
              </div>
            )}

            {/* Tap hint */}
            {!flipped && (
              <div className="absolute bottom-0 inset-x-0 flex justify-center py-4 pointer-events-none"
                style={{ background: 'linear-gradient(to top, rgba(255,255,255,0.7) 0%, transparent 100%)' }}>
                <span className="text-xs text-creamDim/70 tracking-widest uppercase font-mono">
                  Tippen zum Umdrehen
                </span>
              </div>
            )}
          </div>

          {/* BACK — names + notes */}
          <div className="flip-back panel-paper flex flex-col items-center justify-center gap-4 p-8 relative"
            style={{ boxShadow: '0 8px 40px rgba(42,85,52,0.14), 0 2px 12px rgba(0,0,0,0.08)' }}>
            {/* Decorative botanical gradient top */}
            <div className="absolute inset-x-0 top-0 h-24 rounded-t-2xl pointer-events-none"
              style={{ background: 'linear-gradient(180deg, rgba(104,168,126,0.12) 0%, transparent 100%)' }} />

            <div className="relative z-10 flex flex-col items-center gap-4 text-center max-w-md w-full">
              {/* Leaf icon */}
              <div className="w-10 h-10 rounded-full bg-sage/10 flex items-center justify-center border border-sage/20">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-sage">
                  <path d="M17 8C8 10 5.9 16.17 3.82 21.34L5.71 22l1-2.3A4.49 4.49 0 008 20C19 20 22 3 22 3c-1 2-8 3.5-13.5 7"/>
                </svg>
              </div>

              {/* German name */}
              <div>
                <p className="text-xs text-creamDim/60 font-mono uppercase tracking-wider mb-1">
                  Deutscher Name
                </p>
                <h1 className="plant-name text-3xl font-bold text-cream leading-tight">
                  {plant.german_name}
                </h1>
              </div>

              {/* Botanical divider */}
              <div className="botanical-divider w-full text-border/60 text-xs">
                Botanischer Name
              </div>

              {/* Botanical name */}
              <p className="botanical text-2xl text-sage font-medium">
                {plant.botanical_name}
              </p>

              {/* Category tag */}
              <span className="tag text-xs">{plant.category}</span>

              {/* Notes */}
              {plant.notes && (
                <div className="w-full px-4 py-2.5 rounded-xl bg-goldLight/40 border border-gold/20 text-creamDim text-sm leading-relaxed text-left">
                  {plant.notes}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── GEMERKT BUTTON ── */}
      <button
        onClick={(e) => { e.stopPropagation(); handleLearnedClick(); }}
        className={`learned-btn ${isLearned ? 'learned-btn--active' : ''} ${learnedAnim ? 'learned-btn--pop' : ''}`}
        title="Taste M drücken"
      >
        {isLearned ? (
          <>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            Gemerkt!
          </>
        ) : (
          <>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
            </svg>
            Als gemerkt markieren
          </>
        )}
      </button>

      {/* Navigation */}
      <div className="flex items-center gap-3">
        <button onClick={(e) => { e.stopPropagation(); onPrev(); }} className="btn-ghost" disabled={total === 0}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          Zurück
        </button>
        <button onClick={(e) => { e.stopPropagation(); setFlipped((f) => !f); }} className="btn-primary px-6">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M1 4v6h6"/><path d="M23 20v-6h-6"/>
            <path d="M20.49 9A9 9 0 005.64 5.64L1 10M23 14l-4.64 4.36A9 9 0 013.51 15"/>
          </svg>
          Umdrehen
        </button>
        <button onClick={(e) => { e.stopPropagation(); onNext(); }} className="btn-ghost" disabled={total === 0}>
          Weiter
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </button>
      </div>

      <div className="text-center mt-1">
        <p className="text-creamDim/40 text-[10px] font-mono mb-1">
          ← → Pfeiltasten · Leertaste umdrehen · M = gemerkt · Wischen auf Mobile
        </p>
        <p className="text-creamDim/30 text-[9px] uppercase tracking-wider max-w-xs mx-auto">
          Hinweis: Diese App ist rein für den privaten Gebrauch zu Lernzwecken bestimmt.
          Verwendete Bilder können urheberrechtlich geschützt sein.
        </p>
      </div>
    </div>
  );
};
